'use client';

import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchJson } from '@/lib/api/client';
import { regionConfigRequest } from '@/lib/api/keys';
import { publishRegionFormat } from '@/lib/region-format';
import { useAuthSession } from '@/providers/app-runtime';
import { resolveRegionConfig } from '../../../shared/region-settings';
import type { RegionConfig } from '../../../shared/types/region';

const REGION_CONFIG_REQUEST = regionConfigRequest();
// Served until the deployment's own config arrives (and signed out). The US
// presets are what every screen did before region settings existed.
const US_PRESET_CONFIG = resolveRegionConfig('US', {});

type RegionConfigValue = {
  config: RegionConfig;
  /** False while `config` is still the US preset standing in for the
   * deployment's own (until it arrives or the read fails) — region-only
   * controls wait for it. */
  loaded: boolean;
  /** Refetch after an admin changes the region or an override. */
  refresh: () => Promise<void>;
};

const RegionConfigContext = createContext<RegionConfigValue | null>(null);

export function RegionConfigProvider({ children }: { children: ReactNode }) {
  const auth = useAuthSession();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: REGION_CONFIG_REQUEST.key,
    queryFn: () => fetchJson<{ ok: true; config: RegionConfig }>(REGION_CONFIG_REQUEST.path),
    enabled: Boolean(auth.user),
    staleTime: REGION_CONFIG_REQUEST.staleTime,
    refetchOnWindowFocus: false,
  });
  // A failed read settles on the US preset rather than hiding US controls.
  const loaded = query.data !== undefined || query.isError;
  const config = query.data?.config ?? US_PRESET_CONFIG;
  // During render rather than in an effect, so the children this render
  // formats already read the new locale and currency.
  publishRegionFormat(config);
  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: REGION_CONFIG_REQUEST.key }),
    [queryClient]
  );
  const value = useMemo(() => ({ config, loaded, refresh }), [config, loaded, refresh]);
  return <RegionConfigContext.Provider value={value}>{children}</RegionConfigContext.Provider>;
}

export function useRegionConfig(): RegionConfigValue {
  const context = useContext(RegionConfigContext);
  if (!context) {
    throw new Error('useRegionConfig must be used inside RegionConfigProvider');
  }
  return context;
}
